<template>
  <div
    id="my-slider"
    :se-min="minThreshold"
    :se-step="step"
    :se-min-value="min"
    :se-max-value="max"
    :se-max="maxThreshold"
    class="slider"
    :class="{ 'slider--disabled': disabled }"
  >
    <div class="slider-touch-left">
      <span class="slider-thumb"></span>
      <span v-if="showValueChip" class="slider-value-chip">{{ min }}</span>
    </div>
    <div class="slider-touch-right">
      <span class="slider-thumb"></span>
      <span v-if="showValueChip" class="slider-value-chip">{{ max }}</span>
    </div>
    <div class="slider-line">
      <span></span>
    </div>
  </div>
</template>

<script>
import RangeSlider from './RangeSlider'
import { ref, defineComponent, onMounted } from 'vue'
export default defineComponent({
  name: 'DoubleRangeSlider',
  emits: ['trigger','update:min','update:max'],
  props: {
    minThreshold: {
      type: Number,
      default: 100,
    },
    maxThreshold: {
      type: Number,
      default: 1000,
    },
    step: {
      type: Number,
      default: 1,
    },
    min: {
      type: Number,
      required: true,
    },
    max: {
      type: Number,
      required: true,
    },
    // v2 branding (V11) — opt-in: dims + blocks interaction at 40% opacity
    disabled: {
      type: Boolean,
      default: false,
    },
    // v2 branding (V11) — opt-in: dark value-chip bubble above each thumb
    showValueChip: {
      type: Boolean,
      default: false,
    },
  },
  setup: (props, { emit }) => {
    const instance = ref(undefined)

    const updateValues = (min, max) => {
      emit('update:min', parseInt(min))
      emit('update:max', parseInt(max))
      emit('trigger')
    }

    onMounted(() => {
      instance.value = new RangeSlider('my-slider')
      instance.value.didChanged = (min, max) => updateValues(min, max)
    })

    return { updateValues }
  },
})
</script>

<style lang="scss">
.slider {
  display: block;
  position: relative;
  width: 100%;
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;
  -o-user-select: none;
  user-select: none;
  .slider-touch-left,
  .slider-touch-right {
    -webkit-box-sizing: border-box;
    -moz-box-sizing: border-box;
    box-sizing: border-box;
    display: block;
    position: absolute;
    // v2 (V11): 20px thumb; `offsetWidth` here feeds RangeSlider.js position math
    height: 20px;
    width: 20px;
    top: -2px; // re-center 20px thumb on the 4px track (track center = 8px)
    z-index: 2;
    // v2 (V11): 44x44 touch target around the 20px thumb — pseudo, does NOT
    // change offsetWidth (kept at 20px so the JS position math is unaffected).
    &::before {
      content: '';
      position: absolute;
      top: 50%;
      left: 50%;
      width: 44px;
      height: 44px;
      transform: translate(-50%, -50%);
      z-index: -1;
    }
  }
  // v2 (V11): 20px WHITE circle, 2px green border, neutral thumb elevation
  .slider-touch-left .slider-thumb,
  .slider-touch-right .slider-thumb {
    display: block;
    width: 100%;
    height: 100%;
    background: $white; // §9 v2 — white fill (was $brand-green)
    border: $bw-thick solid $brand-green; // 2px green (was 1px)
    border-radius: 50%;
    box-shadow: $elevation-thumb; // v2 neutral 0 2px 6px
    transition: transform $dur-base $ease-out; // v2 hover grow motion
  }
  // v2 (V11): hover → thumb scales to 22px (20px * 1.1)
  .slider-touch-left:hover .slider-thumb,
  .slider-touch-right:hover .slider-thumb {
    transform: scale(1.1);
  }
  // §14 ring if a handle receives focus (drag handles; full keyboard nav = QA).
  // v2 (V11): GREEN ring (was gold), 2px + 2px offset.
  .slider-touch-left:focus-visible .slider-thumb,
  .slider-touch-right:focus-visible .slider-thumb {
    outline: 2px solid $brand-green;
    outline-offset: 2px;
  }
  // v2 (V11): opt-in dark value-chip bubble above the thumb (arrow points down)
  .slider-value-chip {
    position: absolute;
    bottom: calc(100% + 8px);
    left: 50%;
    transform: translateX(-50%);
    padding: 2px 8px;
    background: $ink;
    color: $white;
    font-size: 12px;
    font-weight: 600; // SemiBold
    line-height: 1.4;
    border-radius: $radius-sm;
    white-space: nowrap;
    pointer-events: none;
    &::after {
      content: '';
      position: absolute;
      top: 100%;
      left: 50%;
      transform: translateX(-50%);
      border: 4px solid transparent;
      border-top-color: $ink;
    }
  }
  .slider-line {
    -webkit-box-sizing: border-box;
    -moz-box-sizing: border-box;
    box-sizing: border-box;
    position: absolute;
    width: calc(100% - 15px);
    left: 6px;
    top: 6px;
    height: 4px;
    border-radius: $radius-xs; // 2px
    background: $border-hairline; // v2 (V11): unfilled track = border #E7E1D3 (was rgba green .4)
    z-index: 0;
    overflow: hidden;
    span {
      display: block;
      height: 100%;
      width: 0%;
      background: $brand-green; // v2 filled range = solid green #197660
    }
  }
  // v2 (V11): disabled → 40% opacity (component-specific literal, per L282)
  &.slider--disabled {
    opacity: 0.4;
    pointer-events: none;
  }
}
</style>
